'use client';

/**
 * Соединение двух браузеров для передачи файлов.
 *
 * Здесь живёт вся механика: сигналинг через SSE, канал данных WebRTC,
 * докачка после обрыва и переподключение. Интерфейс получает готовое
 * состояние и ничего не знает про ICE и SDP.
 *
 * Роли неравноправны намеренно. Отправитель создаёт комнату и делает
 * предложение соединения, получатель отвечает. Так проще: обе стороны
 * одновременно предлагающими быть не могут, это ведёт к состоянию гонки в
 * согласовании, которое здесь ничем не оправдано.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FileAssembler,
  decryptChunk,
  keyFromSecret,
  sendFile,
  type ControlMessage,
  type FileMeta,
} from '@/lib/transfer/channel';

/**
 * Публичные серверы STUN.
 *
 * Нужны, чтобы стороны узнали свои внешние адреса. Через них не проходит
 * ни байта содержимого: они сообщают адрес и умолкают. TURN, который
 * пересылал бы трафик через себя, мы намеренно не используем: это
 * противоречило бы обещанию, что файл идёт напрямую.
 */
const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
];

export type Phase =
  | 'idle'
  | 'waiting'
  | 'connecting'
  | 'connected'
  | 'transferring'
  | 'done'
  | 'refused'
  | 'failed';

export interface FileProgress {
  meta: FileMeta;
  /** Передано байт. */
  sent: number;
  /** Готовый файл у получателя. */
  blob?: Blob;
}

interface Options {
  roomId: string;
  secret: string;
  role: 'host' | 'guest';
  /** Файлы отправителя. У получателя пусто. */
  files?: File[];
}

/** Состояние передачи, каким его видит интерфейс. */
export interface TransferState {
  phase: Phase;
  progress: FileProgress[];
  error: string | null;
  /** Была ли передача возобновлена после обрыва. */
  resumed: boolean;
}

export function useTransfer({ roomId, secret, role, files }: Options): TransferState {
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<FileProgress[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [resumed, setResumed] = useState(false);

  const peer = useRef<RTCPeerConnection | null>(null);
  const channel = useRef<RTCDataChannel | null>(null);
  const key = useRef<CryptoKey | null>(null);
  const participantId = useRef<string>('');
  // Подтверждённые смещения: с них передача продолжается после обрыва.
  const acked = useRef<Map<string, number>>(new Map());
  const assemblers = useRef<Map<string, FileAssembler>>(new Map());
  const incoming = useRef<string | null>(null);
  const sending = useRef(false);
  /**
   * Передача завершена целиком.
   *
   * Нужен, чтобы отличить закрытие канала после успешного конца от обрыва
   * посреди работы: в первом случае пересоздавать соединение незачем.
   */
  const finished = useRef(false);
  /**
   * Комната отказала во входе.
   *
   * Отказ окончателен: пересобирать соединение после него бессмысленно, а
   * визуально это выглядело как вечное «устанавливаем соединение» вместо
   * честного объяснения, что ссылкой уже пользуются.
   */
  const refused = useRef(false);
  const filesRef = useRef<File[]>(files ?? []);
  filesRef.current = files ?? [];

  /**
   * Метаданные принимаемых файлов.
   *
   * Держатся в ссылке, а не читаются из состояния: обработчик сообщений
   * создаётся один раз, и через замыкание он видел бы состояние на момент
   * создания, то есть пустой список.
   */
  const progressRef = useRef<FileProgress[]>([]);
  progressRef.current = progress;

  const progressMeta = useCallback(
    (id: string): FileMeta | undefined => progressRef.current.find((p) => p.meta.id === id)?.meta,
    [],
  );

  /** Отправка сигнала второму участнику. */
  const signal = useCallback(
    async (payload: unknown) => {
      await fetch(`/api/transfer/${roomId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'signal', participant: participantId.current, payload }),
      });
    },
    [roomId],
  );

  /** Передаёт все файлы, начиная с подтверждённых смещений. */
  const pump = useCallback(async () => {
    const dc = channel.current;
    const k = key.current;
    if (!dc || !k || sending.current) return;

    sending.current = true;
    setPhase('transferring');

    try {
      for (const file of filesRef.current) {
        const id = fileId(file);
        const offset = acked.current.get(id) ?? 0;
        if (offset >= file.size) continue;

        const meta: FileMeta = { id, name: file.name, size: file.size, type: file.type };

        const sent = await sendFile({
          channel: dc,
          key: k,
          file,
          meta,
          offset,
          isOpen: () => dc.readyState === 'open',
          onProgress: (value) => {
            setProgress((prev) => prev.map((p) => (p.meta.id === id ? { ...p, sent: value } : p)));
          },
        });

        // Канал оборвался посередине: продолжим с этого места, когда
        // соединение восстановится.
        if (sent < file.size) {
          acked.current.set(id, sent);
          return;
        }
      }

      finished.current = true;
      setPhase('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase('failed');
    } finally {
      sending.current = false;
    }
  }, []);

  /** Обработка управляющих сообщений и кусков. */
  const onMessage = useCallback(
    async (event: MessageEvent<unknown>) => {
      const k = key.current;
      if (!k) return;

      // Строка это управление, двоичные данные это кусок файла.
      if (typeof event.data === 'string') {
        const message = JSON.parse(event.data) as ControlMessage;

        switch (message.kind) {
          case 'offer-files': {
            // После обрыва состав приходит заново. Обнулять показанное
            // нельзя: часть файла уже принята, и полоса прыгнула бы назад,
            // хотя данные никуда не делись.
            setProgress((prev) =>
              message.files.map((meta) => {
                const assembler = assemblers.current.get(meta.id);
                const done = prev.find((p) => p.meta.id === meta.id)?.blob;

                return { meta, sent: assembler?.bytes ?? 0, blob: done };
              }),
            );

            // Просим каждый файл с того места, где остановились. При первом
            // подключении это ноль, после обрыва — принятый объём.
            for (const meta of message.files) {
              const assembler = assemblers.current.get(meta.id);

              // Уже собранный файл заново не запрашиваем: он на руках.
              if (assembler?.complete) continue;

              const have = assembler?.bytes ?? 0;
              if (have > 0) setResumed(true);

              const request: ControlMessage = {
                kind: 'request',
                fileId: meta.id,
                offset: have,
              };
              channel.current?.send(JSON.stringify(request));
            }
            break;
          }

          case 'request': {
            acked.current.set(message.fileId, message.offset);
            if (message.offset > 0) setResumed(true);
            void pump();
            break;
          }

          case 'begin': {
            incoming.current = message.fileId;
            break;
          }

          case 'done': {
            const assembler = assemblers.current.get(message.fileId);
            if (assembler) {
              const blob = assembler.blob();
              setProgress((prev) =>
                prev.map((p) => (p.meta.id === message.fileId ? { ...p, blob } : p)),
              );
            }

            incoming.current = null;

            // Все ли файлы собраны: пока нет, ждём следующий.
            setProgress((prev) => {
              if (prev.every((p) => p.blob)) {
                finished.current = true;
                setPhase('done');
              }
              return prev;
            });
            break;
          }

          case 'ack': {
            acked.current.set(message.fileId, message.received);
            break;
          }

          case 'error': {
            setError(message.message);
            setPhase('failed');
            break;
          }
        }

        return;
      }

      // Кусок файла.
      const fileIdNow = incoming.current;
      if (!fileIdNow) return;

      const buffer =
        event.data instanceof ArrayBuffer ? event.data : await (event.data as Blob).arrayBuffer();

      try {
        const plain = await decryptChunk(k, buffer);

        let assembler = assemblers.current.get(fileIdNow);
        if (!assembler) {
          const meta = progressMeta(fileIdNow);
          if (!meta) return;
          assembler = new FileAssembler(meta);
          assemblers.current.set(fileIdNow, assembler);
        }

        assembler.add(plain);

        setProgress((prev) =>
          prev.map((p) => (p.meta.id === fileIdNow ? { ...p, sent: assembler.bytes } : p)),
        );

        const ack: ControlMessage = {
          kind: 'ack',
          fileId: fileIdNow,
          received: assembler.bytes,
        };
        channel.current?.send(JSON.stringify(ack));
      } catch {
        // Кусок не расшифровался: секрет не тот или данные повреждены.
        // Продолжать бессмысленно, файл всё равно будет битым.
        setError('decrypt-failed');
        setPhase('failed');
      }
    },
    [pump, progressMeta],
  );

  useEffect(() => {
    let disposed = false;
    let source: EventSource | null = null;
    let ping: ReturnType<typeof setInterval> | undefined;

    participantId.current = crypto.randomUUID();

    /**
     * Создаёт соединение с нуля.
     *
     * Именно с нуля, а не «чинит» старое: закрытое соединение оживить
     * нельзя, а после обрыва нужно продолжать передачу. Смещения и уже
     * принятые куски живут вне соединения, поэтому пересоздание ничего не
     * теряет: стороны заново договорятся, с какого места продолжить.
     */
    const connect = (): RTCPeerConnection => {
      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      peer.current = pc;

      pc.onicecandidate = (e) => {
        if (e.candidate) void signal({ candidate: e.candidate });
      };

      pc.onconnectionstatechange = () => {
        if (disposed) return;
        if (pc.connectionState === 'connected') setPhase('connected');
      };

      const attach = (dc: RTCDataChannel) => {
        channel.current = dc;
        dc.binaryType = 'arraybuffer';

        dc.onopen = () => {
          setPhase('connected');

          // Отправитель объявляет состав, получатель отвечает запросами с
          // нужными смещениями. Тот же обмен идёт и после обрыва, поэтому
          // отдельной ветки для докачки не нужно: смещение не ноль, и
          // передача продолжается с него.
          if (role === 'host') {
            const message: ControlMessage = {
              kind: 'offer-files',
              files: filesRef.current.map((file) => ({
                id: fileId(file),
                name: file.name,
                size: file.size,
                type: file.type,
              })),
            };
            dc.send(JSON.stringify(message));
          }
        };

        dc.onmessage = (e) => {
          void onMessage(e);
        };

        /**
         * Канал закрылся: связь потеряна посреди передачи.
         *
         * Отправку прекращаем и пересобираем соединение заново. Получатель
         * при этом входит в комнату повторно, и сервер сообщает сторонам,
         * что их снова двое: этого достаточно, чтобы согласование началось
         * с начала, а передача продолжилась с подтверждённого смещения.
         */
        dc.onclose = () => {
          if (disposed) return;

          sending.current = false;
          channel.current = null;

          // Уже всё принято: закрытие после завершения это не обрыв.
          if (finished.current) return;

          // Во входе отказано: соединения быть и не должно.
          if (refused.current) return;

          setResumed(true);
          setPhase('connecting');

          pc.close();
          const next = connect();

          if (role === 'host') {
            void (async () => {
              const offer = await next.createOffer();
              await next.setLocalDescription(offer);
              await signal({ description: offer });
            })();
          }
        };
      };

      if (role === 'host') {
        const dc = pc.createDataChannel('files', { ordered: true });
        attach(dc);
      } else {
        pc.ondatachannel = (e) => {
          attach(e.channel);
        };
      }

      return pc;
    };

    const setup = async () => {
      key.current = await keyFromSecret(secret);
      if (disposed) return;

      if (role === 'host') {
        setProgress(
          filesRef.current.map((file) => ({
            meta: { id: fileId(file), name: file.name, size: file.size, type: file.type },
            sent: 0,
          })),
        );
      }

      connect();

      source = new EventSource(
        `/api/transfer/${roomId}/stream?participant=${participantId.current}`,
      );

      source.onmessage = (e) => {
        void (async () => {
          const event = JSON.parse(e.data as string) as
            | { type: 'peers'; count: number; you: 'host' | 'guest' }
            | { type: 'signal'; from: string; payload: Record<string, unknown> }
            | { type: 'peer-left' }
            | { type: 'refused'; reason: string };

          if (event.type === 'refused') {
            refused.current = true;
            setPhase('refused');

            // Соединение больше не нужно: комната занята, и попытки
            // договориться только мешали бы паре, которая уже работает.
            peer.current?.close();
            peer.current = null;
            source?.close();
            return;
          }

          // Соединение могло смениться после обрыва, поэтому берётся
          // текущее, а не то, что было на момент подписки. Проверка идёт
          // после отказа: там соединения уже нет, и ранний выход проглотил
          // бы единственное сообщение, которое нужно показать человеку.
          const pc = peer.current;
          if (!pc) return;

          if (event.type === 'peers') {
            if (event.count < 2) {
              setPhase('waiting');
              return;
            }

            setPhase('connecting');

            // Предложение делает только отправитель: если предлагать будут
            // обе стороны, согласование войдёт в состояние гонки.
            if (role === 'host') {
              const offer = await pc.createOffer();
              await pc.setLocalDescription(offer);
              await signal({ description: offer });
            }

            return;
          }

          if (event.type === 'peer-left') {
            // Второй ушёл. Это не провал: смещения сохранены, и когда он
            // вернётся по той же ссылке, передача продолжится.
            setPhase('waiting');
            return;
          }

          // Осталось только событие сигналинга: остальные разобраны выше.
          const { description, candidate } = event.payload as {
            description?: RTCSessionDescriptionInit;
            candidate?: RTCIceCandidateInit;
          };

          if (description) {
            await pc.setRemoteDescription(description);

            if (description.type === 'offer') {
              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);
              await signal({ description: answer });
            }
          }

          if (candidate) {
            try {
              await pc.addIceCandidate(candidate);
            } catch {
              // Кандидат пришёл раньше описания: такое бывает и не
              // мешает соединению, оставшиеся кандидаты его поднимут.
            }
          }
        })();
      };

      // Сервер считает молчащего участника ушедшим, а он может просто
      // долго передавать файл.
      ping = setInterval(() => {
        void fetch(`/api/transfer/${roomId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'ping', participant: participantId.current }),
        });
      }, 20000);
    };

    void setup();

    return () => {
      disposed = true;
      if (ping) clearInterval(ping);
      source?.close();
      channel.current?.close();
      peer.current?.close();
    };
  }, [roomId, secret, role, signal, onMessage]);

  return { phase, progress, error, resumed };
}

/**
 * Идентификатор файла.
 *
 * Считается из имени, размера и даты изменения, а не случайный: после
 * перезагрузки страницы человек выбирает тот же файл заново, и докачка
 * должна его узнать.
 */
function fileId(file: File): string {
  return `${file.name}:${String(file.size)}:${String(file.lastModified)}`;
}
